import React from 'react';
import classNames from 'classnames';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { BaseInputProps } from './BaseInput';
import useT from '../../../../translation/src/hooks/useT';

export const RangeInput: React.FC<BaseInputProps<number>> = ({ instance, onChange, disabled = false, className }) => {
  const { config, value } = instance;
  const mixedStateTooltip = useT('Different values selected', { swc: true });

  // Get min/max values from validation config
  const minValue = config.validation?.min ?? 0;
  const maxValue = config.validation?.max ?? 100;
  const currentValue: number = typeof value === 'number' && !isNaN(value) ? value : minValue;
  const showRangeLabels = config.ui?.showRangeLabels ?? true; // Default to true for backwards compatibility
  const isDisabled = disabled || config.disabled;

  // Determine decimal places based on step value
  const step = config.validation?.step || 0.01;
  const decimalPlaces = step >= 1 ? 0 : Math.max(0, -Math.floor(Math.log10(step)));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = parseFloat(e.target.value);
    onChange(isNaN(numValue) ? minValue : Math.max(minValue, Math.min(maxValue, numValue)));
  };

  return (
    <div className={classNames('flex flex-col', className)}>
      <div className="relative flex-1">
        {/* Add spacing above slider when labels are shown */}
        {showRangeLabels && <div className="h-2" />}

        <input
          type="range"
          value={currentValue}
          onChange={handleChange}
          disabled={isDisabled}
          min={minValue}
          max={maxValue}
          step={config.validation?.step || 0.01}
          className={classNames('w-full accent-neon-green-700 dark:accent-neon-green-300', 'h-2 slider-thumb:h-4 slider-thumb:w-4', { 'opacity-50': isDisabled || instance.isMixed })}
          style={{
            margin: '0', // Remove margin to center properly
          }}
        />

        {/* Add spacing below slider when labels are shown */}
        {showRangeLabels && <div className="h-2" />}

        {instance.isMixed && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <QuestionMarkCircleIcon className="w-5 h-5 text-neon-green-500 bg-white bg-opacity-80 rounded-full cursor-help" title={mixedStateTooltip} />
          </div>
        )}
      </div>

      {showRangeLabels && (
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>{minValue}</span>
          <span className="font-medium text-gray-700 dark:text-gray-300">
            {currentValue.toFixed(decimalPlaces)}
            {config.ui?.customUnit && <span className="ml-1 font-normal">{config.ui.customUnit}</span>}
          </span>
          <span>{maxValue}</span>
        </div>
      )}
    </div>
  );
};
